import React, { Suspense, useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { HashUrlRedirect } from './components/HashUrlRedirect';
import { PropertyRedirect } from './components/PropertyRedirect';
import { getProjectUrl } from './lib/slug';

// Hooks
import { useAppAuth } from './hooks/useAppAuth';
import { useProperties } from './hooks/useProperties';
import { useGlobalUI } from './hooks/useGlobalUI';
import { api } from './services/api';
import { isSupabaseConfigured } from './lib/supabase';
import SupabaseConfigError from './components/SupabaseConfigError';
import { authGate } from './lib/authGate';
import { Property } from './types';

// Layout
import AppShell from './components/layout/AppShell';
import SoftGateModal from './components/SoftGateModal';
import AuthGateModal from './components/AuthGateModal';
import UpgradeModal from './components/UpgradeModal';

// Auth
import { ProtectedRoute, ProfileGate } from './components/auth/ProtectedRoute';
import { GatedRoute } from './components/auth/GatedRoute';
import { useAuth } from './components/auth/AuthProvider';
import AuthCallback from './pages/AuthCallback';

// Lazy Views
const DashboardView = React.lazy(() => import('./components/DashboardView'));
const PropertyDetailsPage = React.lazy(() => import('./components/PropertyDetailsPage'));
const AdminView = React.lazy(() => import('./components/AdminView'));
const LoginView = React.lazy(() => import('./components/LoginView'));
const OnboardingView = React.lazy(() => import('./components/OnboardingView'));
const NotFound = React.lazy(() => import('./components/NotFound'));
// LandingView removed - root now redirects to marketplace

// Google Ads Sitelink Pages
const HotProjectsPage = React.lazy(() => import('./pages/HotProjectsPage'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const ROICalculatorPage = React.lazy(() => import('./pages/ROICalculatorPage'));

// DEV-only components - use consistent lazy loading pattern to avoid hook ordering issues
const RoutingTrace = React.lazy(() => 
  import.meta.env.DEV 
    ? import('./components/dev/RoutingTrace')
    : Promise.resolve({ default: () => null })
);
const MobileUIAuditPage = React.lazy(() => 
  import.meta.env.DEV 
    ? import('./components/dev/MobileUIAuditPage')
    : Promise.resolve({ default: () => null })
);
const ShortlistView = React.lazy(() => import('./components/ShortlistView'));
// Property Finder disabled until later
// const PropertyFinderView = React.lazy(() => import('./components/PropertyFinderView'));
const ProfileView = React.lazy(() => import('./components/ProfileView'));
const SettingsView = React.lazy(() => import('./components/SettingsView'));

// Tools
const ROICalculatorView = React.lazy(() => import('./components/ROICalculatorView'));
const RentalYieldCalculatorView = React.lazy(() => import('./components/RentalYieldCalculatorView'));
const MortgageCalculatorView = React.lazy(() => import('./components/MortgageCalculatorView'));
const MarketAnalysisView = React.lazy(() => import('./components/MarketAnalysisView'));
const PortfolioView = React.lazy(() => import('./components/PortfolioView'));
const DeveloperHubView = React.lazy(() => import('./components/DeveloperHubView'));
// LandingView removed - root now redirects to marketplace
const CompareView = React.lazy(() => import('./components/CompareView'));
const SecureUnitPage = React.lazy(() => import('./components/SecureUnitPage'));

// Root redirect component - safely redirects to marketplace
const RootRedirect: React.FC = () => {
  const navigate = useNavigate();
  
  React.useEffect(() => {
    navigate('/marketplace', { replace: true });
  }, [navigate]);
  
  return null;
};

// Login route guard: redirect to dashboard if already logged in
const LoginRouteGuard: React.FC = () => {
  const { authReady, user } = useAuth();
  const { authStatus } = useAppAuth();
  const navigate = useNavigate();
  const redirectDoneRef = React.useRef(false);

  // Use useEffect to redirect - NEVER use Navigate in render (causes hard reloads)
  React.useEffect(() => {
    if (!authReady) return;

    // If auth is ready and user is logged in, redirect to marketplace (ONCE)
    if ((user || authStatus === 'logged_in') && !redirectDoneRef.current) {
      redirectDoneRef.current = true;
      navigate('/marketplace', { replace: true });
    }
  }, [authReady, user, authStatus, navigate]);

  // Wait for auth to be ready before making redirect decisions
  if (!authReady) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  // If redirect is happening, show loading
  if (redirectDoneRef.current) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  // Otherwise show login page
  return <LoginView onLoginSuccess={() => navigate('/marketplace')} />;
};

// --- MAIN APP COMPONENT ---
export default function App() {
  // HOOKS
  const {
    user,
    isGuest,
    authLoading,
    shortlist,
    setShortlist,
    savedReports,
    setSavedReports,
    handleLogout,
    userProfile,
    setUserProfile
  } = useAppAuth();

  const {
    properties: displayProperties,
    allPropertiesRaw,
    isLoading: projectsLoading,
    filters,
    setFilters,
    resetFilters,
    sortOption,
    setSortOption,
    currentPage,
    setCurrentPage,
    totalPages,
    setAllProperties,
    hasLoadedOnce
  } = useProperties();

  const {
    currency,
    setCurrency,
    language,
    setLanguage,
    viewMode,
    setViewMode,
    isMobileFiltersOpen,
    setIsMobileFiltersOpen,
    isGateOpen,
    setIsGateOpen: setShowGate,
    isSortMenuOpen,
    setIsSortMenuOpen,
    convertPrice
  } = useGlobalUI();

  const navigate = useNavigate();
  const location = useLocation();
  const [compareList, setCompareList] = useState<(string | number)[]>([]);
  const [isAuthGateOpen, setIsAuthGateOpen] = useState(false);
  const [isInvestorAccountModalOpen, setIsInvestorAccountModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  // Register auth gate callback (for logged-out users)
  useEffect(() => {
    authGate.setGateOpenCallback((reason, action) => {
      // Only open auth gate modal if user is logged out
      if (isGuest) {
        setIsAuthGateOpen(true);
      }
    });
    return () => authGate.setGateOpenCallback(null);
  }, [isGuest]);

  // Listen for investor account modal trigger (for logged-in non-investors)
  useEffect(() => {
    const handleOpenInvestorAccountModal = (event: CustomEvent) => {
      setIsInvestorAccountModalOpen(true);
    };

    window.addEventListener('openInvestorAccountModal', handleOpenInvestorAccountModal as EventListener);
    return () => {
      window.removeEventListener('openInvestorAccountModal', handleOpenInvestorAccountModal as EventListener);
    };
  }, []);

  // Handlers
  const handleFilterChange = (newFilters: any) => {
    setFilters((prev: any) => ({ ...prev, ...newFilters }));
  };

  const handleSearch = (query: string) => {
    handleFilterChange({ searchQuery: query });
    if (location.pathname !== '/marketplace') {
      navigate('/marketplace');
    }
  };

  const handleToggleShortlist = async (id: string | number) => {
    const idStr = String(id);
    
    // Gate shortlist toggle for guests (consistent across mobile and desktop)
    if (isGuest) {
      authGate.openGate('shortlist', {
        path: location.pathname,
        action: 'shortlist',
        propertyId: idStr
      });
      return;
    }

    const userId = user?.id ? String(user.id) : undefined;
    const wasShortlisted = shortlist.some(i => String(i) === idStr);

    // Optimistic update
    if (wasShortlisted) {
      setShortlist(prev => prev.filter(i => String(i) !== idStr));
    } else {
      setShortlist(prev => [...prev, idStr]);
    }

    try {
      // API handles logged-in (Supabase) mode
      const updatedList = await api.user.toggleShortlist(userId, idStr);
      // Update state with the returned list (ensures consistency)
      setShortlist(updatedList.map(id => String(id)));
      if (import.meta.env.DEV) {
        console.log('[App] Shortlist toggled successfully');
      }
    } catch (e: any) {
      if (import.meta.env.DEV) {
        console.error("[App] Shortlist toggle failed", e);
      }
      // Rollback optimistic update
      if (wasShortlisted) {
        setShortlist(prev => [...prev, idStr]);
      } else {
        setShortlist(prev => prev.filter(i => String(i) !== idStr));
      }
    }
  };

  const isShortlisted = (id: string | number) => shortlist.some(s => String(s) === String(id));

  const handleToggleCompare = (id: string | number) => {
    const idStr = String(id);
    setCompareList(prev => {
      if (prev.some(i => String(i) === idStr)) {
        return prev.filter(i => String(i) !== idStr);
      }
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const handleRemoveCompare = (id: string | number) => {
    const idStr = String(id);
    setCompareList(prev => prev.filter(i => String(i) !== idStr));
  };

  const handleAddCompare = (id: string | number) => {
    const idStr = String(id);
    setCompareList(prev => {
      if (prev.some(i => String(i) === idStr) || prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const handleSaveReport = async (report: any) => {
    setSavedReports(prev => [report, ...prev]);
    if (user?.id) {
      try {
        await api.user.saveReport(String(user.id), report);
      } catch (e) {
        console.error('Failed to save report to backend', e);
      }
    }
  };

  useEffect(() => {
    if (shortlist.length === 0 && compareList.length > 0) {
      setCompareList([]);
      return;
    }
    setCompareList(prev => {
      const filtered = prev.filter(id => shortlist.some(s => String(s) === String(id)));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [shortlist, compareList]);

  // Prevent guests from accessing pages beyond page 1
  useEffect(() => {
    if (isGuest && currentPage > 1) {
      setCurrentPage(1);
    }
  }, [isGuest, currentPage, setCurrentPage]);

  // Admin Handlers (Optimistic Updates)
  const handleAddProperty = (p: any) => setAllProperties(prev => [...prev, p]);
  const handleUpdateProperty = (p: any) => setAllProperties(prev => prev.map(item => item.id === p.id ? p : item));
  const handleDeleteProperty = (id: string | number) => setAllProperties(prev => prev.filter(item => item.id !== id));

  const compareProperties = allPropertiesRaw.filter(p =>
    compareList.some(id => String(id) === String(p.id))
  );

  const handlePropertyClick = (id: string | number, property?: Property) => {
    if (property) {
      navigate(getProjectUrl(property));
    } else {
      // Fallback to ID route (will redirect to slug route)
      navigate(`/property/${id}`);
    }
  };

  // Handle page changes - prevent guests from going beyond page 1
  const handlePageChange = (page: number) => {
    if (isGuest && page > 1) {
      authGate.openGate('pagination', {
        path: location.pathname,
        action: 'pagination'
      });
      return;
    }
    setCurrentPage(page);
  };

  // Show fallback UI if Supabase config is missing
  if (!isSupabaseConfigured()) {
    return <SupabaseConfigError />;
  }

  if (authLoading) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-slate-50 gap-4">
        <Loader2 className="animate-spin text-blue-600" size={32} />
        <p className="text-slate-500 font-medium">Loading Marketplace...</p>
      </div>
    );
  }

  return (
    <AppShell
      isGuest={isGuest}
      shortlistCount={shortlist.length}
      filters={filters}
      onFilterChange={handleFilterChange}
      onSearch={handleSearch}
      onLogout={handleLogout}
      isMobileFiltersOpen={isMobileFiltersOpen}
      onPropertyClick={handlePropertyClick}
      setIsMobileFiltersOpen={setIsMobileFiltersOpen}
      triggerGate={() => setShowGate(true)}
      userProfile={userProfile}
    >
      <HashUrlRedirect />
      <Suspense fallback={
        <div className="h-96 w-full flex items-center justify-center">
          <Loader2 className="animate-spin text-slate-300" size={24} />
        </div>
      }>
        <Routes>
          {/* Redirect root to marketplace */}
          <Route path="/" element={<RootRedirect />} />

          <Route path="/login" element={<LoginRouteGuard />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          <Route path="/onboarding" element={<OnboardingView />} />

          {/* Google Ads Sitelink Routes */}
          <Route path="/hotprojects" element={<HotProjectsPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/roi-calculator" element={<ROICalculatorPage />} />

          {/* Redirect /dashboard to /marketplace for backwards compatibility */}
          <Route path="/dashboard" element={<Navigate to="/marketplace" replace />} />

          {/* Public Marketplace - Browse without auth */}
          <Route path="/marketplace" element={
              <DashboardView
                isGuest={isGuest}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                sortOption={sortOption}
                onSort={setSortOption}
                isSortMenuOpen={isSortMenuOpen}
                onToggleSortMenu={() => setIsSortMenuOpen(!isSortMenuOpen)}
                triggerGate={() => setShowGate(true)}
                displayProperties={displayProperties}
                shortlist={shortlist}
                onToggleShortlist={handleToggleShortlist}
                handlePropertyClick={(id, property) => {
                  if (property) {
                    navigate(getProjectUrl(property));
                  } else {
                    navigate(`/property/${id}`);
                  }
                }}
                convertPrice={convertPrice}
                onClearFilters={resetFilters}
                onToggleFilters={() => setIsMobileFiltersOpen(true)}
                onSearch={handleSearch}
                searchQuery={filters.searchQuery || ''}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isLoading={projectsLoading}
                hasLoadedOnce={hasLoadedOnce}
              />
          } />

          {/* SEO-friendly project route */}
          <Route path="/projects/:slug" element={
              <PropertyDetailsPage
                isShortlisted={isShortlisted}
                onToggleShortlist={handleToggleShortlist}
                convertPrice={convertPrice}
                currency={currency}
              />
          } />

          {/* Legacy /property/:id route - redirects to slug route */}
          <Route path="/property/:id" element={<PropertyRedirect />} />

          <Route path="/_secure-unit/:id" element={
            <ProtectedRoute>
              <SecureUnitPage />
            </ProtectedRoute>
          } />

          {/* Account & Tools */}
          <Route path="/account" element={
            <ProtectedRoute>
              <ProfileView
                userProfile={userProfile}
                onSaveProfile={async (profile) => { await setUserProfile(profile); }}
                onNavigateToProperty={(id, property) => {
                  if (property) {
                    navigate(getProjectUrl(property));
                  } else {
                    navigate(`/property/${id}`);
                  }
                }}
                onToggleShortlist={handleToggleShortlist}
                shortlist={shortlist}
              />
            </ProtectedRoute>
          } />

          <Route path="/settings" element={
            <ProtectedRoute>
              <SettingsView
                currency={currency}
                setCurrency={setCurrency}
                language={language}
                setLanguage={setLanguage}
              />
            </ProtectedRoute>
          } />

          <Route path="/shortlist" element={
            <ProtectedRoute>
              <ShortlistView
                shortlistIds={shortlist}
                shortlistProperties={allPropertiesRaw.filter(p => isShortlisted(p.id))}
                compareList={compareList}
                savedReports={savedReports}
                onToggleShortlist={async (id) => {
                  if (import.meta.env.DEV) {
                    console.log('[App] Shortlist toggle called from ShortlistView:', id, 'Current shortlist:', shortlist);
                  }
                  await handleToggleShortlist(id);
                  if (import.meta.env.DEV) {
                    console.log('[App] Shortlist after toggle:', shortlist);
                  }
                }}
                onToggleCompare={handleToggleCompare}
                onNavigateToCompare={() => navigate('/compare')}
                onViewDetails={(id, property) => {
                  if (property) {
                    navigate(getProjectUrl(property));
                  } else {
                    navigate(`/property/${id}`);
                  }
                }}
                onNavigateHome={() => navigate('/marketplace')}
              />
            </ProtectedRoute>
          } />

          <Route path="/compare" element={
            <ProtectedRoute>
              <CompareView
                properties={compareProperties}
                shortlistProperties={allPropertiesRaw.filter(p => isShortlisted(p.id))}
                onRemove={handleRemoveCompare}
                onAdd={handleAddCompare}
                onBack={() => navigate('/shortlist')}
                onViewDetails={(id, property) => {
                  if (property) {
                    navigate(getProjectUrl(property));
                  } else {
                    navigate(`/property/${id}`);
                  }
                }}
                onSaveReport={handleSaveReport}
                userProfile={userProfile}
                convertPrice={convertPrice}
              />
            </ProtectedRoute>
          } />

          <Route path="/portfolio" element={<ProtectedRoute><PortfolioView /></ProtectedRoute>} />

          {/* Property Finder route disabled until later */}
          <Route path="/finder" element={<Navigate to="/marketplace" replace />} />

          <Route path="/tools/*" element={
            <Routes>
              {/* ROI Calculator - Gated (Investor Account Required) */}
              <Route path="calculator" element={
                <GatedRoute
                  gateTitle="Investor Account Required"
                  gateMessage="Create a free investor account to access advanced investment tools, ROI calculators, and market analysis."
                >
                  <ROICalculatorView
                    shortlist={shortlist}
                    allProperties={allPropertiesRaw}
                    onNavigateHome={() => navigate('/marketplace')}
                    currency={currency}
                    convertPrice={convertPrice}
                  />
                </GatedRoute>
              } />
              {/* Rental Yield - Open to all logged-in users */}
              <Route path="rental-yield" element={
                <ProtectedRoute>
                  <RentalYieldCalculatorView
                    shortlist={shortlist}
                    allProperties={allPropertiesRaw}
                    onNavigateHome={() => navigate('/marketplace')}
                    currency={currency}
                    convertPrice={convertPrice}
                  />
                </ProtectedRoute>
              } />
              {/* Mortgage Estimator - Gated (Investor Account Required) */}
              <Route path="mortgage" element={
                <GatedRoute
                  gateTitle="Investor Account Required"
                  gateMessage="Create a free investor account to access advanced investment tools, ROI calculators, and market analysis."
                >
                  <MortgageCalculatorView
                    onNavigateHome={() => navigate('/marketplace')}
                    currency={currency}
                  />
                </GatedRoute>
              } />
            </Routes>
          } />

          <Route path="/analytics" element={
            <GatedRoute
              gateTitle="Investor Account Required"
              gateMessage="Create a free investor account to access market analysis, trends, and investment insights."
            >
              <MarketAnalysisView properties={allPropertiesRaw} />
            </GatedRoute>
          } />

          <Route path="/developer-hub" element={
            <GatedRoute
              gateTitle="Investor Account Required"
              gateMessage="Create a free investor account to access developer intelligence, due diligence, and track records."
            >
              <DeveloperHubView
                properties={allPropertiesRaw}
                onNavigateToProperty={(id, property) => {
                  if (property) {
                    navigate(getProjectUrl(property));
                  } else {
                    navigate(`/property/${id}`);
                  }
                }}
                onNavigateHome={() => navigate('/marketplace')}
                onNavigateToMarketplace={(developerName) => {
                  handleFilterChange({ developer: developerName });
                  navigate('/marketplace');
                }}
                convertPrice={convertPrice}
              />
            </GatedRoute>
          } />

          {/* Admin */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminView
                properties={allPropertiesRaw}
                onAddProperty={handleAddProperty}
                onUpdateProperty={handleUpdateProperty}
                onDeleteProperty={handleDeleteProperty}
                onLogout={handleLogout}
                onSwitchToClient={() => navigate('/marketplace')}
              />
            </ProtectedRoute>
          } />

          {/* DEV-only mobile audit route */}
          {import.meta.env.DEV && (
            <Route path="/__mobile-audit" element={<MobileUIAuditPage />} />
          )}

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      <SoftGateModal
        isOpen={isGateOpen}
        onClose={() => setShowGate(false)}
        onLogin={() => navigate('/login')}
        onSignup={() => navigate('/login')}
      />

      {/* Auth Gate Modal for logged-out users (sign-in/sign-up) */}
      <AuthGateModal
        isOpen={isAuthGateOpen}
        onClose={() => {
          setIsAuthGateOpen(false);
          setPendingAction(null);
        }}
        onAuthSuccess={() => {
          // Execute pending action after auth success
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
        gateTitle={
          (() => {
            const returnTo = authGate.getReturnTo();
            if (!returnTo) return undefined;
            const action = returnTo.action || '';
            // For logged-out users, show sign-in modal (not investor account message)
            // Investor account message is only for logged-in users without investor account
            return undefined; // Always show default "Create a free account" for logged-out users
          })()
        }
        gateMessage={
          (() => {
            const returnTo = authGate.getReturnTo();
            if (!returnTo) return undefined;
            // For logged-out users, show sign-in modal (not investor account message)
            return undefined; // Always show default message for logged-out users
          })()
        }
      />

      {/* Investor Account Modal for logged-in users without investor account */}
      <UpgradeModal
        open={isInvestorAccountModalOpen}
        onClose={() => setIsInvestorAccountModalOpen(false)}
        source="investor_tools"
      />

      {/* DEV-only routing trace overlay */}
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <RoutingTrace />
        </Suspense>
      )}
    </AppShell>
  );
}
